import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '@/App';
import {
  Plus, Pencil, Trash2, GripVertical, Eye, EyeOff,
  Upload, X, ChevronUp, ChevronDown, Users
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';

// ── Language catalogue ────────────────────────────────────────────────────────
const LANGUAGES = [
  { code: 'gb', label: 'English',    flag: '🇬🇧' },
  { code: 'fi', label: 'Finnish',    flag: '🇫🇮' },
  { code: 'sv', label: 'Swedish',    flag: '🇸🇪' },
  { code: 'es', label: 'Spanish',    flag: '🇪🇸' },
  { code: 'de', label: 'German',     flag: '🇩🇪' },
  { code: 'fr', label: 'French',     flag: '🇫🇷' },
  { code: 'nl', label: 'Dutch',      flag: '🇳🇱' },
  { code: 'ru', label: 'Russian',    flag: '🇷🇺' },
  { code: 'it', label: 'Italian',    flag: '🇮🇹' },
  { code: 'pt', label: 'Portuguese', flag: '🇵🇹' },
  { code: 'pl', label: 'Polish',     flag: '🇵🇱' },
  { code: 'no', label: 'Norwegian',  flag: '🇳🇴' },
  { code: 'da', label: 'Danish',     flag: '🇩🇰' },
  { code: 'et', label: 'Estonian',   flag: '🇪🇪' },
  { code: 'lv', label: 'Latvian',    flag: '🇱🇻' },
  { code: 'lt', label: 'Lithuanian', flag: '🇱🇹' },
  { code: 'hu', label: 'Hungarian',  flag: '🇭🇺' },
];

const flagForCode = (code) =>
  LANGUAGES.find((l) => l.code === code)?.flag ?? code.toUpperCase();

// ── UI-language tabs on the Team member edit dialog ─────────────────────────
// English is the source of truth — the top-level role_title/description
// fields ARE the English copy. Other languages live inside `translations`
// and fall back to English on the public site when empty.
const EDIT_LANGS = [
  { code: 'en', flag: '🇬🇧', label: 'English' },
  { code: 'es', flag: '🇪🇸', label: 'Español' },
  { code: 'fi', flag: '🇫🇮', label: 'Suomi'   },
  { code: 'sv', flag: '🇸🇪', label: 'Svenska' },
  { code: 'de', flag: '🇩🇪', label: 'Deutsch' },
];

// ── Blank form ────────────────────────────────────────────────────────────────
const BLANK = {
  name:         '',
  role_title:   '',
  description:  '',
  photo:        null,
  languages:    [],
  sort_order:   0,
  is_active:    true,
  // Nested per-language overrides for role_title + description. Keys match
  // the UI language codes above (excluding 'en' — English is the source).
  translations: { es: {}, fi: {}, sv: {}, de: {} },
};

// ── Member preview card (matches the website design) ─────────────────────────
const MemberCard = ({ member, onEdit, onDelete, onToggleActive, onMoveUp, onMoveDown, isFirst, isLast }) => (
  <div className={`bg-white rounded-2xl shadow-sm border overflow-hidden flex flex-col transition-all duration-200 ${
    member.is_active ? 'border-gray-100' : 'border-dashed border-gray-300 opacity-60'
  }`}>
    {/* Photo */}
    <div className="relative bg-[#8fb5b8] h-52 flex-shrink-0 overflow-hidden">
      {member.photo ? (
        <img
          src={member.photo}
          alt={member.name}
          className="w-full h-full object-cover"
        />
      ) : (
        <div className="w-full h-full flex items-center justify-center">
          <Users className="w-16 h-16 text-white/50" />
        </div>
      )}
      {/* Status badge */}
      {!member.is_active && (
        <div className="absolute top-2 left-2">
          <Badge variant="secondary" className="text-xs bg-gray-800/70 text-white border-0">
            Hidden
          </Badge>
        </div>
      )}
    </div>

    {/* Info */}
    <div className="p-5 flex flex-col flex-1 text-center">
      <h3 className="font-bold text-gray-900 text-lg leading-tight mb-1">{member.name}</h3>
      <p className="text-[#c0774a] text-sm font-medium mb-3">{member.role_title}</p>
      <p className="text-gray-500 text-sm leading-relaxed flex-1 mb-4">{member.description}</p>

      {/* Flags */}
      {member.languages?.length > 0 && (
        <div className="flex items-center justify-center gap-1.5 mb-4 flex-wrap">
          {member.languages.map((code) => (
            <span key={code} className="text-xl" title={LANGUAGES.find(l => l.code === code)?.label}>
              {flagForCode(code)}
            </span>
          ))}
        </div>
      )}

      {/* Actions */}
      <div className="flex items-center justify-between gap-2 mt-auto pt-2 border-t border-gray-100">
        {/* Reorder */}
        <div className="flex gap-1">
          <button
            onClick={onMoveUp}
            disabled={isFirst}
            className="p-1.5 rounded text-gray-400 hover:text-gray-700 hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            title="Move up"
          >
            <ChevronUp className="w-4 h-4" />
          </button>
          <button
            onClick={onMoveDown}
            disabled={isLast}
            className="p-1.5 rounded text-gray-400 hover:text-gray-700 hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            title="Move down"
          >
            <ChevronDown className="w-4 h-4" />
          </button>
        </div>

        {/* Edit / Visibility / Delete */}
        <div className="flex gap-1">
          <button
            onClick={onToggleActive}
            className="p-1.5 rounded text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors"
            title={member.is_active ? 'Hide from website' : 'Show on website'}
          >
            {member.is_active ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
          </button>
          <button
            onClick={onEdit}
            className="p-1.5 rounded text-gray-400 hover:text-[#194a50] hover:bg-[#194a50]/10 transition-colors"
            title="Edit"
          >
            <Pencil className="w-4 h-4" />
          </button>
          <button
            onClick={onDelete}
            className="p-1.5 rounded text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors"
            title="Delete"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  </div>
);

// ── Main Page ─────────────────────────────────────────────────────────────────
export default function TeamPage() {
  const [members, setMembers]       = useState([]);
  const [loading, setLoading]       = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);   // null = create, else member obj
  const [form, setForm]             = useState(BLANK);
  const [saving, setSaving]         = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null); // member to delete
  // Active UI-language tab in the edit dialog ('en' | 'es' | 'fi' | 'sv' | 'de')
  const [editLang, setEditLang]     = useState('en');
  const photoRef = useRef(null);

  // ── Fetch ───────────────────────────────────────────────────────────────────
  const fetchMembers = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/team/admin`);
      setMembers(res.data || []);
    } catch {
      toast.error('Failed to load team members');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchMembers(); }, []);

  // ── Dialog helpers ──────────────────────────────────────────────────────────
  const openCreate = () => {
    setEditTarget(null);
    setForm({ ...BLANK, sort_order: members.length, translations: { es: {}, fi: {}, sv: {}, de: {} } });
    setEditLang('en');
    setDialogOpen(true);
  };

  const openEdit = (member) => {
    setEditTarget(member);
    // Merge existing translations into the default shape so every tab
    // always has a sub-object, even if the backend returned none.
    const mergedTranslations = { es: {}, fi: {}, sv: {}, de: {}, ...(member.translations || {}) };
    setForm({
      name:         member.name,
      role_title:   member.role_title,
      description:  member.description,
      photo:        member.photo,
      languages:    member.languages || [],
      sort_order:   member.sort_order,
      is_active:    member.is_active,
      translations: mergedTranslations,
    });
    setEditLang('en');
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setEditTarget(null);
    setForm(BLANK);
    setEditLang('en');
  };

  // ── Per-language field getters/setters ────────────────────────────────────
  // `en` reads/writes the top-level role_title/description fields (the
  // source of truth). Any other language reads/writes translations[code].
  const getLangField = (code, field) => {
    if (code === 'en') return form[field] || '';
    return (form.translations?.[code]?.[field]) || '';
  };

  const setLangField = (code, field, value) => {
    if (code === 'en') {
      setForm((f) => ({ ...f, [field]: value }));
      return;
    }
    setForm((f) => ({
      ...f,
      translations: {
        ...(f.translations || {}),
        [code]: {
          ...(f.translations?.[code] || {}),
          [field]: value,
        },
      },
    }));
  };

  // ── Photo upload ────────────────────────────────────────────────────────────
  const handlePhotoChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Photo must be under 5 MB');
      return;
    }
    const reader = new FileReader();
    reader.onload = (ev) => setForm((f) => ({ ...f, photo: ev.target.result }));
    reader.readAsDataURL(file);
  };

  // ── Language toggle ─────────────────────────────────────────────────────────
  const toggleLanguage = (code) => {
    setForm((f) => ({
      ...f,
      languages: f.languages.includes(code)
        ? f.languages.filter((c) => c !== code)
        : [...f.languages, code],
    }));
  };

  // ── Save (create or update) ─────────────────────────────────────────────────
  const handleSave = async () => {
    if (!form.name.trim())       { toast.error('Name is required'); return; }
    if (!form.role_title.trim()) { toast.error('Role title is required (English is the source)'); return; }

    // Strip empty fields from the translations object so MongoDB stays clean.
    // The backend's PUT handler merges field-by-field, so sending a sparse
    // object is fine — only populated fields are persisted.
    const cleanTranslations = {};
    ['es', 'fi', 'sv', 'de'].forEach((code) => {
      const t = form.translations?.[code] || {};
      const sub = {};
      if (t.role_title && t.role_title.trim())   sub.role_title  = t.role_title.trim();
      if (t.description && t.description.trim()) sub.description = t.description.trim();
      if (Object.keys(sub).length) cleanTranslations[code] = sub;
    });
    const payload = { ...form, translations: cleanTranslations };

    setSaving(true);
    try {
      if (editTarget) {
        await axios.put(`${API}/team/${editTarget.id}`, payload);
        toast.success('Team member updated');
      } else {
        await axios.post(`${API}/team`, payload);
        toast.success('Team member created');
      }
      closeDialog();
      fetchMembers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  // ── Delete ──────────────────────────────────────────────────────────────────
  const handleDelete = async (member) => {
    try {
      await axios.delete(`${API}/team/${member.id}`);
      toast.success(`${member.name} removed`);
      setDeleteConfirm(null);
      fetchMembers();
    } catch {
      toast.error('Delete failed');
    }
  };

  // ── Toggle visibility ───────────────────────────────────────────────────────
  const handleToggleActive = async (member) => {
    try {
      await axios.put(`${API}/team/${member.id}`, { is_active: !member.is_active });
      toast.success(member.is_active ? 'Hidden from website' : 'Now visible on website');
      fetchMembers();
    } catch {
      toast.error('Update failed');
    }
  };

  // ── Reorder (swap adjacent) ─────────────────────────────────────────────────
  const handleMove = async (index, direction) => {
    const swapIdx = index + direction;
    if (swapIdx < 0 || swapIdx >= members.length) return;

    const updated = [...members];
    [updated[index], updated[swapIdx]] = [updated[swapIdx], updated[index]];

    // Assign sequential sort_order
    const reordered = updated.map((m, i) => ({ ...m, sort_order: i }));
    setMembers(reordered);                    // optimistic update

    try {
      await axios.put(`${API}/team/reorder`, reordered.map(({ id, sort_order }) => ({ id, sort_order })));
    } catch {
      toast.error('Reorder failed');
      fetchMembers();                         // revert on error
    }
  };

  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <div className="animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 font-['Outfit']">Team Members</h1>
          <p className="text-gray-500 text-sm mt-1">
            Manage the team profiles shown on your website — photos, bios, and languages.
          </p>
        </div>
        <Button onClick={openCreate} className="bg-[#194a50] hover:bg-[#10555e] gap-2">
          <Plus className="w-4 h-4" /> Add Member
        </Button>
      </div>

      {/* Stats bar */}
      <div className="flex items-center gap-4 mb-6 text-sm text-gray-500">
        <span>{members.length} member{members.length !== 1 ? 's' : ''} total</span>
        <span>·</span>
        <span className="text-green-600">{members.filter(m => m.is_active).length} visible</span>
        {members.filter(m => !m.is_active).length > 0 && (
          <>
            <span>·</span>
            <span className="text-gray-400">{members.filter(m => !m.is_active).length} hidden</span>
          </>
        )}
      </div>

      {/* Loading */}
      {loading && (
        <div className="flex items-center justify-center py-20">
          <div className="spinner" />
        </div>
      )}

      {/* Empty state */}
      {!loading && members.length === 0 && (
        <div className="flex flex-col items-center justify-center py-20 text-center">
          <div className="w-16 h-16 rounded-full bg-[#194a50]/10 flex items-center justify-center mb-4">
            <Users className="w-8 h-8 text-[#194a50]" />
          </div>
          <h3 className="text-lg font-semibold text-gray-700 mb-1">No team members yet</h3>
          <p className="text-gray-400 text-sm mb-6">Add your first team member to get started.</p>
          <Button onClick={openCreate} className="bg-[#194a50] hover:bg-[#10555e] gap-2">
            <Plus className="w-4 h-4" /> Add First Member
          </Button>
        </div>
      )}

      {/* Cards grid */}
      {!loading && members.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {members.map((member, idx) => (
            <MemberCard
              key={member.id}
              member={member}
              isFirst={idx === 0}
              isLast={idx === members.length - 1}
              onEdit={() => openEdit(member)}
              onDelete={() => setDeleteConfirm(member)}
              onToggleActive={() => handleToggleActive(member)}
              onMoveUp={() => handleMove(idx, -1)}
              onMoveDown={() => handleMove(idx, 1)}
            />
          ))}
        </div>
      )}

      {/* ── Create / Edit Dialog ─────────────────────────────────────────────── */}
      <Dialog open={dialogOpen} onOpenChange={(o) => { if (!o) closeDialog(); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-['Outfit'] text-[#194a50]">
              {editTarget ? 'Edit Team Member' : 'Add Team Member'}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-6 py-2">
            {/* Photo upload */}
            <div>
              <Label className="text-sm font-medium text-gray-700 mb-2 block">Photo</Label>
              <div className="flex items-start gap-4">
                {/* Preview */}
                <div
                  className="w-28 h-28 rounded-xl overflow-hidden bg-[#8fb5b8] flex-shrink-0 flex items-center justify-center cursor-pointer hover:opacity-90 transition-opacity"
                  onClick={() => photoRef.current?.click()}
                  title="Click to upload photo"
                >
                  {form.photo ? (
                    <img src={form.photo} alt="Preview" className="w-full h-full object-cover" />
                  ) : (
                    <Users className="w-10 h-10 text-white/60" />
                  )}
                </div>

                <div className="flex-1 space-y-2">
                  <div className="flex gap-2 flex-wrap">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => photoRef.current?.click()}
                      className="gap-2"
                    >
                      <Upload className="w-4 h-4" /> Upload Photo
                    </Button>
                    {form.photo && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => setForm((f) => ({ ...f, photo: null }))}
                        className="gap-2 text-red-500 hover:text-red-600 hover:bg-red-50"
                      >
                        <X className="w-4 h-4" /> Remove
                      </Button>
                    )}
                  </div>
                  <p className="text-xs text-gray-400">
                    Recommended: 480 × 560 px • Max 5 MB • JPG or PNG
                  </p>
                  {/* URL input as alternative */}
                  <Input
                    placeholder="Or paste an image URL…"
                    value={form.photo && !form.photo.startsWith('data:') ? form.photo : ''}
                    onChange={(e) => setForm((f) => ({ ...f, photo: e.target.value || null }))}
                    className="text-xs h-8"
                  />
                </div>
              </div>
              <input
                ref={photoRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handlePhotoChange}
              />
            </div>

            {/* Name — language-independent (people's names don't translate) */}
            <div className="space-y-1.5">
              <Label htmlFor="tm-name">Full Name <span className="text-red-500">*</span></Label>
              <Input
                id="tm-name"
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                placeholder="e.g. Maya Kallio"
              />
            </div>

            {/* ── Language-specific fields (Role Title + Description) ──────────── */}
            <div className="space-y-3 border border-gray-100 rounded-xl p-4 bg-gray-50/50">
              {/* Tab strip */}
              <div className="flex items-center gap-1.5 flex-wrap">
                {EDIT_LANGS.map((lang) => {
                  const isActive = editLang === lang.code;
                  const hasValue = lang.code === 'en'
                    ? !!(form.role_title || form.description)
                    : !!((form.translations?.[lang.code]?.role_title) || (form.translations?.[lang.code]?.description));
                  return (
                    <button
                      key={lang.code}
                      type="button"
                      onClick={() => setEditLang(lang.code)}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-xs font-medium transition-all ${
                        isActive
                          ? 'border-[#194a50] bg-[#194a50] text-white'
                          : hasValue
                            ? 'border-[#194a50]/30 bg-white text-[#194a50] hover:border-[#194a50]'
                            : 'border-dashed border-gray-300 bg-white text-gray-400 hover:border-gray-400'
                      }`}
                      title={hasValue ? `${lang.label} — has content` : `${lang.label} — empty (falls back to English)`}
                    >
                      <span className="text-sm">{lang.flag}</span>
                      {lang.label}
                    </button>
                  );
                })}
              </div>

              <p className="text-[11px] text-gray-400 leading-tight">
                English is required and is the source of truth. Other languages are optional — empty fields fall back to English on the public site.
              </p>

              {/* Role Title */}
              <div className="space-y-1.5">
                <Label htmlFor="tm-role">
                  Role Title {editLang === 'en' && <span className="text-red-500">*</span>}
                </Label>
                <Input
                  id="tm-role"
                  value={getLangField(editLang, 'role_title')}
                  onChange={(e) => setLangField(editLang, 'role_title', e.target.value)}
                  placeholder={editLang === 'en'
                    ? 'e.g. The Heart of Our Operations'
                    : `${EDIT_LANGS.find(l => l.code === editLang)?.label} translation (leave blank to use English)`}
                />
              </div>

              {/* Description */}
              <div className="space-y-1.5">
                <Label htmlFor="tm-desc">Description</Label>
                <Textarea
                  id="tm-desc"
                  value={getLangField(editLang, 'description')}
                  onChange={(e) => setLangField(editLang, 'description', e.target.value)}
                  placeholder={editLang === 'en'
                    ? 'A short bio shown below the name on the website…'
                    : `${EDIT_LANGS.find(l => l.code === editLang)?.label} translation (leave blank to use English)`}
                  rows={3}
                  className="resize-none"
                />
                <p className="text-xs text-gray-400 text-right">
                  {getLangField(editLang, 'description').length}/1000
                </p>
              </div>
            </div>

            {/* Languages */}
            <div className="space-y-2">
              <Label>Languages Spoken</Label>
              <div className="flex flex-wrap gap-2">
                {LANGUAGES.map((lang) => {
                  const selected = form.languages.includes(lang.code);
                  return (
                    <button
                      key={lang.code}
                      type="button"
                      onClick={() => toggleLanguage(lang.code)}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-sm font-medium transition-all duration-150 ${
                        selected
                          ? 'border-[#194a50] bg-[#194a50]/10 text-[#194a50]'
                          : 'border-gray-200 text-gray-500 hover:border-gray-400'
                      }`}
                    >
                      <span className="text-base">{lang.flag}</span>
                      {lang.label}
                    </button>
                  );
                })}
              </div>
              {form.languages.length > 0 && (
                <p className="text-xs text-gray-400 mt-1">
                  Selected: {form.languages.map(flagForCode).join(' ')}
                </p>
              )}
            </div>

            {/* Visibility toggle */}
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
              <div>
                <p className="font-medium text-gray-700 text-sm">Visible on website</p>
                <p className="text-xs text-gray-400 mt-0.5">
                  Hidden members are saved but not shown publicly.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setForm((f) => ({ ...f, is_active: !f.is_active }))}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${
                  form.is_active ? 'bg-[#194a50]' : 'bg-gray-300'
                }`}
              >
                <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                  form.is_active ? 'translate-x-6' : 'translate-x-1'
                }`} />
              </button>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={closeDialog} disabled={saving}>
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={saving}
              className="bg-[#194a50] hover:bg-[#10555e]"
            >
              {saving ? 'Saving…' : editTarget ? 'Save Changes' : 'Add Member'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Delete Confirmation Dialog ────────────────────────────────────────── */}
      <Dialog open={!!deleteConfirm} onOpenChange={(o) => { if (!o) setDeleteConfirm(null); }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Remove Team Member?</DialogTitle>
          </DialogHeader>
          <p className="text-gray-500 text-sm py-2">
            This will permanently remove{' '}
            <span className="font-semibold text-gray-800">{deleteConfirm?.name}</span>{' '}
            from the team. This action cannot be undone.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteConfirm(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => handleDelete(deleteConfirm)}
            >
              Remove
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
